/**
 * Valider une créance client d'ouverture (400000) — et décider ce qu'il en
 * advient au 01/01/2026 (VALIDÉ ÉCLOSION, 26/09/2026).
 *
 * - **Ordinaire** : elle reste en 400000.
 * - **Douteuse** : elle existe toujours juridiquement mais est compromise →
 *   407000, avec sa réduction de valeur en 409000 (au plus le douteux). Aucune
 *   charge en 2026 : la dépréciation appartient à la situation au 31/12/2025.
 * - **Éteinte** : déjà définitivement abandonnée avant l'ouverture → elle
 *   n'entre pas dans l'ouverture ; le contrôle clients la montre à part.
 *
 * Les parts se combinent (une partie douteuse, une partie éteinte) ; le reste
 * demeure en 400000. Le choix est justifié et attribué à qui le fait.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { messageErreur } from '../../../lib/payments/erreur';
import { validerUneCreance } from '../../../lib/compta/reprise/gateway.supabase';
import { controlerTraitement, effetsDuTraitement, type LigneDeReprise, type Traitement } from '../../../lib/compta/reprise/types';
import SaisieMontant from '../components/SaisieMontant';
import { euros } from '../components/money';

export default function TraitementCreance({ ligne, apresGeste, montantModifiable }: {
  ligne: LigneDeReprise;
  apresGeste: () => void;
  /** Une ligne calculée peut être validée à un autre montant, justifié ; une saisie se corrige par sa saisie. */
  montantModifiable: boolean;
}) {
  const initial = ligne.montantValideCents ?? ligne.montantCalculeCents ?? 0;
  const [t, setT] = useState<Traitement>({
    montantCents: initial,
    douteuxCents: ligne.douteuxCents,
    reductionValeurCents: ligne.reductionValeurCents,
    eteintCents: ligne.eteintCents,
    justification: ligne.justification ?? '',
  });
  const [envoi, setEnvoi] = useState(false);
  const refus = controlerTraitement(t);
  const ecartAuCalcul = ligne.montantCalculeCents !== null && t.montantCents !== ligne.montantCalculeCents;
  const justificationRequise = ligne.aRevoir !== null || ecartAuCalcul || t.douteuxCents > 0 || t.eteintCents > 0;
  const f = effetsDuTraitement(t);

  const valider = async () => {
    setEnvoi(true);
    try {
      await validerUneCreance(ligne.id, t);
      toast.success('Créance validée');
      apresGeste();
    } catch (e) {
      toast.error(messageErreur(e), { duration: 8000 });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">Créance au 31/12/2025
          <SaisieMontant valeurCents={t.montantCents} onChange={(c) => setT({ ...t, montantCents: c })}
            disabled={!montantModifiable} className="w-32" ariaLabel="Montant de la créance" />
        </label>
        {montantModifiable && ecartAuCalcul && (
          <button type="button" className="text-xs text-indigo-700 hover:underline"
            onClick={() => setT({ ...t, montantCents: ligne.montantCalculeCents ?? 0 })}>
            revenir au calcul ({euros(ligne.montantCalculeCents ?? 0)})
          </button>
        )}
      </div>
      {montantModifiable && ecartAuCalcul && (
        <p className="text-xs text-amber-800">
          S’écarter du calcul fait apparaître un écart avec le grand livre des familles, qui bloque la validation.
          Une créance compromise se traite en douteuse ; une créance abandonnée, en éteinte.
        </p>
      )}

      <fieldset className="grid gap-3 rounded border border-gray-200 bg-white p-3 sm:grid-cols-3">
        <legend className="px-1 text-xs uppercase tracking-wide text-gray-500">À l’ouverture</legend>
        <label className="space-y-1">
          <span className="block">Part douteuse (407000)</span>
          <SaisieMontant valeurCents={t.douteuxCents} className="w-32" ariaLabel="Part douteuse"
            onChange={(c) => setT({ ...t, douteuxCents: c, reductionValeurCents: Math.min(t.reductionValeurCents || c, c) })} />
        </label>
        <label className="space-y-1">
          <span className="block">Réduction de valeur (409000)</span>
          <SaisieMontant valeurCents={t.reductionValeurCents} className="w-32" ariaLabel="Réduction de valeur"
            disabled={t.douteuxCents === 0} onChange={(c) => setT({ ...t, reductionValeurCents: c })} />
        </label>
        <label className="space-y-1">
          <span className="block">Part éteinte avant l’ouverture</span>
          <SaisieMontant valeurCents={t.eteintCents} className="w-32" ariaLabel="Part éteinte"
            onChange={(c) => setT({ ...t, eteintCents: c })} />
        </label>
        <p className="text-xs text-gray-500 sm:col-span-3">
          Reste en 400000 : <strong className="tabular-nums">{euros(Math.max(f.normalCents, 0))}</strong>
          {f.douteuxCents > 0 && <> · 407000 : <span className="tabular-nums">{euros(f.douteuxCents)}</span></>}
          {f.reductionValeurCents > 0 && <> · 409000 : <span className="tabular-nums">{euros(f.reductionValeurCents)}</span> (aucune charge en 2026)</>}
          {f.eteintCents > 0 && <> · hors ouverture : <span className="tabular-nums">{euros(f.eteintCents)}</span></>}
        </p>
      </fieldset>

      <label className="block">
        Justification{justificationRequise && <span className="text-red-600"> (obligatoire)</span>}
        <textarea value={t.justification} rows={2} onChange={(e) => setT({ ...t, justification: e.target.value })}
          placeholder="Douteuse : procédure, insolvabilité… Éteinte : abandon décidé le…, prescription…"
          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm" />
      </label>
      {refus.length > 0 && <ul className="list-disc pl-5 text-xs text-amber-800">{refus.map((r) => <li key={r}>{r}</li>)}</ul>}
      <button type="button" onClick={valider}
        disabled={envoi || refus.length > 0 || (justificationRequise && t.justification.trim() === '')}
        className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
        {envoi && <Loader2 className="h-4 w-4 animate-spin" />} Valider la créance
      </button>
    </div>
  );
}
